import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  DEFAULT_TOASTER_LIFE,
  ISummaryPillItem,
  IWizardContext,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnSelectQueryParams,
  ZnSingleSelect,
} from '@zendra/shared';
import { tap } from 'rxjs';

import type { IStepSelectEstablishmentData } from '../interfaces/IStepSelectEstablishmentData';
import { StandFusionCatalogs } from '../services/stand-fusion-catalogs';

type SelectValue = string | string[] | null;

@Component({
  selector: 'lib-step-select-establishment',
  standalone: true,
  imports: [LocalizationPipe, ZnSingleSelect],
  templateUrl: './step-select-establishment.html',
})
export class StepSelectEstablishment
  implements WizardStepComponent<IStepSelectEstablishmentData>
{
  readonly establishmentSelect = viewChild('establishmentSelect', { read: ZnSingleSelect });

  private readonly toaster = inject(ToasterService);
  private readonly catalogs = inject(StandFusionCatalogs);
  private readonly loadedEstablishmentLabels = new Map<string, string>();

  readonly localizationKeyPrefix = 'Geographic::StandFusion.StepSelectEstablishment';
  readonly localizationResource = ZendraLocalizationServices.LanguageService;

  readonly selectedEstablishmentId = signal<string | null>(null);
  readonly selectedEstablishmentName = signal<string | null>(null);
  readonly selectedEstablishmentOptions = computed(() => {
    const id = this.selectedEstablishmentId();
    const name = this.selectedEstablishmentName();

    if (!id || !name) {
      return [];
    }

    return [{ key: id, value: name }];
  });

  readonly establishmentOptionsLoader = (params?: ZnSelectQueryParams) =>
    this.catalogs.loadPropertyOptions(params).pipe(
      tap(response => {
        response.items.forEach(option => {
          this.loadedEstablishmentLabels.set(String(option.key), option.value);
        });
      }),
    );

  onActivate(context: IWizardContext): void {
    context.patchStepState(context.activeStepId(), {
      visited: true,
    });

    const currentState =
      context.getStepState<IStepSelectEstablishmentData>(context.activeStepId())?.data;

    this.selectedEstablishmentId.set(currentState?.establishmentId ?? null);
    this.selectedEstablishmentName.set(currentState?.establishmentName ?? null);

    if (currentState?.establishmentId && currentState.establishmentName) {
      this.loadedEstablishmentLabels.set(
        currentState.establishmentId,
        currentState.establishmentName,
      );
    }

    queueMicrotask(() => {
      this.establishmentSelect()?.writeValue(this.selectedEstablishmentId());
    });
  }

  onEstablishmentChange(value: SelectValue): void {
    const establishmentId = Array.isArray(value) ? (value[0] ?? null) : value;

    if (!establishmentId) {
      this.selectedEstablishmentId.set(null);
      this.selectedEstablishmentName.set(null);
      return;
    }

    const name =
      this.establishmentSelect()?.getLabelForId(establishmentId) ||
      this.loadedEstablishmentLabels.get(establishmentId) ||
      null;

    this.selectedEstablishmentId.set(establishmentId);
    this.selectedEstablishmentName.set(name);
  }

  canLeave(): boolean {
    if (this.selectedEstablishmentId()) {
      return true;
    }

    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::StandFusion.StepSelectEstablishment.SelectionRequired`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
    return false;
  }

  canLeaveSync(): boolean {
    return true;
  }

  hasMeaningfulData(): boolean {
  return (
    !!this.selectedEstablishmentId() ||
    !!this.establishmentSelect()?.search().trim()
  );
}

  collect(context: IWizardContext): IStepSelectEstablishmentData {
    const data: IStepSelectEstablishmentData = {
      establishmentId: this.selectedEstablishmentId(),
      establishmentName: this.selectedEstablishmentName(),
    };

    context.patchStepState(context.activeStepId(), {
      visited: true,
      valid: !!this.selectedEstablishmentId(),
      completed: true,
      data,
    });

    return data;
  }

  buildSummary(data: IStepSelectEstablishmentData): ISummaryPillItem[] {
    return data.establishmentName
      ? [{ label: 'Planning::Entity.Property', value: data.establishmentName }]
      : [];
  }
}
